import { useMemo, useState } from "react";
import {
  api,
  type EngagementParts,
  type EngagementSummary,
  type FolderListing,
  type Person,
  type TeamMember,
} from "../lib/api";
import { roleLabel } from "../lib/session";
import { useAction } from "../lib/useResource";
import { Button, Field, Label, Select } from "../components/primitives";

/**
 * Setting an engagement up — the five steps, and the one control that brings
 * people into it.
 *
 * **Why a self-ticking status rather than a wizard.** The pattern that works
 * is a checklist that reads real state and marks itself off as you work, not
 * a sequence you click through once and can never see again. Three to five
 * steps, a quick win first, and — the part that matters here — nothing to
 * tick by hand. Every step below is computed from the database: a step is
 * done because the thing exists, not because somebody said it was.
 *
 * That also means the header is honest after the fact. Come back in a month
 * and it still tells you this engagement has no files, because it still has
 * no files.
 */

export interface SetupState {
  engagement: EngagementSummary;
  parts?: EngagementParts;
  team?: TeamMember[];
  folders?: FolderListing[];
}

interface Step {
  title: string;
  done: boolean;
  detail: string;
  /** What to do about it, when it is the next unmet step. */
  next: string;
}

export function setupSteps({
  engagement,
  parts,
  team,
  folders,
}: SetupState): Step[] {
  const partCount =
    (parts?.workstreams.length ?? 0) + (parts?.milestones.length ?? 0);
  const consultants =
    team?.filter((m) => m.kind === "consultant" && m.status === "accepted") ??
    [];
  const offered = team?.filter((m) => m.kind === "consultant") ?? [];
  const clients = team?.filter((m) => m.kind === "client") ?? [];
  const documents =
    folders?.reduce((n, f) => n + f.documents.length, 0) ?? 0;

  return [
    {
      title: "Opened",
      done: true,
      detail: `Filed as ${engagement.reference}`,
      next: "",
    },
    {
      title: "Shaped",
      done: partCount > 0,
      detail: partCount > 0 ? `${partCount} parts` : "No workstreams yet",
      next: "Give it workstreams and milestones, so work has somewhere to sit.",
    },
    {
      title: "Staffed",
      done: consultants.length > 0,
      detail:
        consultants.length > 0
          ? `${consultants.length} working`
          : offered.length > 0
            ? `${offered.length} offered, none accepted`
            : "Nobody assigned",
      next:
        offered.length > 0
          ? "An assignment is waiting to be accepted. Access starts after acceptance."
          : "Add a consultant below and choose what they can access.",
    },
    {
      title: "Filed",
      done: documents > 0,
      detail: documents > 0 ? `${documents} documents` : "No files yet",
      next: "Upload the engagement letter or brief into Files.",
    },
    {
      title: "Live to the client",
      done: Boolean(engagement.portalActivatedAt),
      detail: engagement.portalActivatedAt
        ? "Portal open"
        : clients.length === 0
          ? "No client contact yet"
          : engagement.portalReadyAt
            ? "Ready, not opened"
            : "Not prepared",
      next:
        clients.length === 0
          ? "Add a client contact before opening the portal."
          : engagement.portalReadyAt
            ? "Open the portal — the client will be notified."
          : "Mark the engagement ready, then open the portal.",
    },
  ];
}

/**
 * The summary at the top: where this engagement is, and the one thing to do
 * next. Deliberately not a wall of tiles — five marks and a sentence.
 */
export function SetupHeader({ state }: { state: SetupState }) {
  const steps = useMemo(() => setupSteps(state), [state]);
  const doneCount = steps.filter((s) => s.done).length;
  const nextStep = steps.find((s) => !s.done);

  return (
    <section className="mt-6 border border-line-strong p-5 sm:p-6">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <Label as="h2">Setting up</Label>
        <span className="font-mono text-xs tabular-nums text-secondary">
          {doneCount} of {steps.length}
        </span>
      </div>

      {/* The marks. A step is done because the thing exists, never because
          somebody ticked it. */}
      <ol className="mt-4 flex flex-wrap gap-x-6 gap-y-3">
        {steps.map((s) => (
          <li key={s.title} className="flex items-baseline gap-2">
            <span
              aria-hidden
              className={`inline-block h-2 w-2 shrink-0 ${
                s.done ? "bg-ink" : "border border-line-strong"
              }`}
            />
            <span className={s.done ? "text-sm text-ink" : "text-sm text-secondary"}>
              {s.title}
            </span>
            <span className="text-xs text-muted">{s.detail}</span>
          </li>
        ))}
      </ol>

      <p className="mt-5 max-w-[62ch] border-t border-line pt-4 text-sm text-ink">
        {nextStep ? (
          <>
            <span className="label">Next</span>{" "}
            <span className="ml-2">{nextStep.next}</span>
          </>
        ) : (
          "Everything is set up. This engagement is running."
        )}
      </p>
    </section>
  );
}

/**
 * Bringing somebody into this engagement.
 *
 * One control, not two. People is the firm's register — everyone invited,
 * with their role — and an engagement draws from it: you pick a person, then
 * say what they are *here*. That was previously two forms in two places,
 * which is why "No consultant available" could appear under the client list
 * and read as though it were about clients.
 *
 * A consultant becomes an assignment, which names this engagement and
 * carries its own scope and end date. A client contact is registered against
 * the client company — and which engagements they actually see is decided by
 * which portals you open, so adding them here does not reveal your other
 * matters for the same client. That seam is stated on screen rather than
 * hidden, because the one way to get it wrong is to assume otherwise.
 */
/**
 * A date the user picked, turned into the moment that day ends.
 *
 * `<input type="date">` yields "2026-10-19", and `new Date("2026-10-19")`
 * parses that as **midnight UTC** — the *start* of the day, in a timezone
 * that is probably not theirs. So "access ends 19 October" was being sent
 * as "access ended at 00:00 on the 19th": pick today and the assignment is
 * expired before it is saved. The consultant accepts it, `app.reach`
 * returns 0 because `expires_at > now()` is false, and the engagement
 * never appears for them — with no error anywhere to say why.
 *
 * Splitting the string and building a local Date gives the end of that day
 * in the user's own timezone, which is what picking a date means.
 */
function endOfDay(date: string): string | null {
  if (!date) return null;
  const [y, m, d] = date.split("-").map(Number);
  if (!y || !m || !d) return null;
  // Local time on purpose: the Lead picked a day on their own calendar.
  return new Date(y, m - 1, d, 23, 59, 59, 999).toISOString();
}

export function BringSomeoneIn({
  engagementId,
  organisation,
  organisationId,
  people,
  existing,
  workstreams = [],
  onDone,
}: {
  engagementId: string;
  organisation: string;
  organisationId?: string;
  people: Person[];
  existing: TeamMember[];
  /**
   * Needed to scope an assignment to one of them. Defaulted rather than
   * required so the two existing call sites keep working; an empty list
   * just means the workstream option explains why it is unavailable.
   */
  workstreams?: { id: string; name: string }[];
  onDone: () => void;
}) {
  const [capacity, setCapacity] = useState<"consultant" | "client">(
    "consultant",
  );
  const [userId, setUserId] = useState("");
  const [scope, setScope] = useState("engagement");
  const [workstreamId, setWorkstreamId] = useState("");
  const [expiresAt, setExpiresAt] = useState("");

  const already = new Set(existing.map((m) => m.userId));

  const candidates = people.filter((p) => {
    if (p.isSuspended || already.has(p.id)) return false;
    return capacity === "client" ? p.role === "client" : p.role !== "client";
  });

  const { invoke, pending, refusal } = useAction(async () => {
    if (capacity === "consultant") {
      await api.post(`/api/engagements/${engagementId}/assignments`, {
        consultantId: userId,
        scope,
        // The route refuses a workstream-scoped assignment that does not
        // name a workstream, and the form never sent one — so "One
        // workstream" was an option that could only ever be rejected.
        workstreamId: scope === "workstream" ? workstreamId : undefined,
        expiresAt: endOfDay(expiresAt),
      });
    } else {
      await api.post(`/api/organisations/${organisationId}/clients`, { userId });
    }
    setUserId("");
    setExpiresAt("");
    setWorkstreamId("");
    onDone();
  });

  return (
    <section className="mt-10 border-t border-line pt-7">
      <Label as="h2">Bring someone into this engagement</Label>

      {!userId && (
        <p className="mt-2 max-w-[62ch] text-sm text-secondary">
          Choose someone from People, then select their role on this
          engagement.
        </p>
      )}

      {/* Capacity first, because it changes who is worth offering. */}
      <div className="mt-5 flex gap-2">
        {(["consultant", "client"] as const).map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => {
              setCapacity(c);
              setUserId("");
            }}
            className={`min-h-11 border px-4 text-sm transition-colors ${
              capacity === c
                ? "border-ink bg-ink text-canvas"
                : "border-line-strong text-ink hover:bg-ink/[0.06]"
            }`}
          >
            {c === "consultant" ? "As a consultant" : "As a client contact"}
          </button>
        ))}
      </div>

      {candidates.length === 0 ? (
        <p className="mt-5 max-w-[62ch] text-sm text-secondary">
          {capacity === "consultant"
            ? "Everyone firm-side is already on this engagement, or nobody has been invited yet. Invite them from People."
            : `Everyone at ${organisation} is already registered, or nobody with the Client role has been invited yet. Invite them from People.`}
        </p>
      ) : (
        <>
          <div className="mt-5 grid max-w-3xl gap-5 sm:grid-cols-2">
            <Select
              label="Person"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
            >
              <option value="">Choose…</option>
              {candidates.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.fullName} · {roleLabel(p.role)}
                </option>
              ))}
            </Select>

            {capacity === "consultant" && (
              <Select
                label="Access"
                hint="Choose which part of the engagement they can see."
                value={scope}
                onChange={(e) => setScope(e.target.value)}
              >
                <option value="engagement">The whole engagement</option>
                <option value="workstream" disabled={workstreams.length === 0}>
                  One workstream
                  {workstreams.length === 0 ? " — none exist yet" : ""}
                </option>
                {/* Reworded. "Individual tasks only" read as "pick which
                    tasks", and there is nothing to pick: an assignment
                    carries a workstream_id and no task_id. This scope is
                    the floor — reach 1 — which means they see the tasks
                    assigned to them and nothing else about the
                    engagement. Saying that is the fix; a task picker
                    would be inventing a column. */}
                <option value="task">Only the tasks assigned to them</option>
              </Select>
            )}
          </div>

          {/* Appears only when it is the thing being asked for. A select
              that is present and irrelevant is how a form teaches people
              to skim past the one field that matters. */}
          {capacity === "consultant" && scope === "workstream" && (
            <div className="mt-5 max-w-sm">
              <Select
                label="Which workstream"
                hint="They will see this stream and nothing else on the engagement."
                value={workstreamId}
                onChange={(e) => setWorkstreamId(e.target.value)}
              >
                <option value="">Choose one</option>
                {workstreams.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.name}
                  </option>
                ))}
              </Select>
            </div>
          )}

          {capacity === "consultant" && (
            <div className="mt-5 max-w-sm">
              <Field
                label="Access ends"
                type="date"
                hint="Leave blank only when access should not expire."
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
              />
            </div>
          )}

          {capacity === "client" && userId && (
            <p className="mt-5 max-w-[62ch] text-sm text-secondary">
              They will see this engagement once its portal is open, and no
              other engagement of {organisation} unless you open that one too.
            </p>
          )}

          {refusal && (
            <p className="mt-5 border-l-2 border-danger pl-4 text-sm text-ink">
              {refusal.message}
            </p>
          )}

          <Button
            tone="primary"
            className="mt-7"
            loading={pending}
            disabled={!userId || (capacity === "client" && !organisationId)}
            onClick={() => void invoke()}
          >
            {capacity === "consultant"
              ? "Offer the assignment"
              : "Add as a client contact"}
          </Button>
        </>
      )}
    </section>
  );
}
